/**
 * useViewerId — a stable ID for the current visitor, used by the
 * "You just viewed" personalisation.
 *
 * Signed-in users: their `user_id` (from `/api/auth/me` cached elsewhere)
 * Anonymous users: a random cookie/localStorage id generated on first use.
 *
 * Never leaks PII — the id is opaque to the backend.
 */
import { useEffect, useState } from "react";
import { getToken } from "@/lib/api";

const KEY = "socibaba.viewer.id";

function readOrCreate(): string {
  try {
    const existing = localStorage.getItem(KEY);
    if (existing && existing.length >= 8) return existing;
    const fresh = `anon_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    localStorage.setItem(KEY, fresh);
    return fresh;
  } catch {
    // localStorage disabled — fall through to a session-only id.
    return `anon_${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function useViewerId(): string {
  const [id, setId] = useState<string>(() => readOrCreate());
  useEffect(() => {
    // If the user signs in later, upgrade the id from anon → real.
    if (!getToken()) return;
    // Backend accepts any string ≥ 6 chars; keeping the anon id is fine
    // for now and preserves history if the user comes back logged out.
  }, []);
  return id;
}
